import Edit16 from '@carbon/icons-react/es/edit/16';
import { Input, Tooltip } from 'antd';
import { Moment } from 'moment';
import React, { FC, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { TimesheetEntryType } from 'api/timesheets';
import { Category } from 'api/timesheets/categories';
import { Button } from 'components/atoms/button';
import { Loading } from 'components/atoms/loading';
import { Popover } from 'components/atoms/popover';
import { formatTime } from 'components/organisms/timesheets/common/format-time';
import {
  isTimeEntryInCreating,
  isTimeEntryPartiallyFilled,
} from 'components/organisms/timesheets/common/utils';
import { BodyShort01 } from 'components/styles';
import { TimesheetTableTimeEntry, TimesheetWeeklyRowTask } from '../../types';
import { AddTimeEntry } from './add-time-entry';
import { TimeDurationInput } from './time-duration-input';

const Loader = styled(Loading)``;

const SecondaryRow = styled.div`
  display: flex;
  justify-content: flex-end;
  visibility: hidden;
`;

const Wrapper = styled.div`
  cursor: pointer;
  padding: calc(calc(var(--spacing-05) * 1) - 1px) var(--spacing-04);
  position: relative;
  height: var(--spacing-09);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  flex-direction: column;

  &:hover ${SecondaryRow} {
    visibility: visible;
  }

  ${Loader} {
    position: absolute;
    bottom: var(--spacing-02);
    right: var(--spacing-02);
  }
`;

const TimeInputBox = styled(TimeDurationInput)`
  height: 18px;
  min-width: 0;
  padding: 0;
  border: none;
  outline: none;
  background: transparent;
  margin-top: 0;
  text-align: left;
  &:focus {
    border: none;
    outline: none;
  }
  &:focus ~ ${SecondaryRow} {
    visibility: hidden;
  }
  &::placeholder {
    font-size: 13px;
  }
`;
const StaticInput = styled(Input)``;
const Time = styled.div`
  ${BodyShort01};
  ${StaticInput} {
    height: 18px;
    min-width: 0;
    padding: 0;
    border: none;
    outline: none;
    background: transparent;
    margin-top: 0;
    text-align: left;
    caret-color: transparent;
    pointer-events: none;
    color: var(--gray-100);
    &:focus {
      border: none;
      outline: none;
      box-shadow: none;
    }
    &::selection {
      background-color: transparent;
      color: inherit;
    }
  }
`;

const PopoverTrigger = styled(Button)`
  position: absolute;
  right: 0;
  bottom: 0;
  color: #000;
  &:hover {
    color: var(--blue-60);
  }
`;

const getTotalMinutes = (childTimeEntries: TimesheetTableTimeEntry[]) => {
  return childTimeEntries.reduce(
    (total, childEntry) => total + childEntry.minutes,
    0,
  );
};

const getNextCellToBeFocussed = () => {
  const cells = Array.from(document.querySelectorAll('[data-time-cell]'));
  const currentIndex = cells.findIndex((cell) => cell.matches(':focus-within'));

  return currentIndex === cells.length - 1 ? cells[0] : cells[currentIndex + 1];
};

export const TimeEntryCell: FC<{
  timeEntries?: TimesheetTableTimeEntry[];
  task: TimesheetWeeklyRowTask;
  timeEntryCategories: Category[];
  day: Moment;
  editable: boolean;
  updateDayEntry: (entryAt: string, timeEntry: TimesheetTableTimeEntry) => void;
  deleteDayEntry: (entryAt: string, timeEntryId: number) => void;
  addDayEntry: (entryAt: string) => void;
  onActivate: (isActive: boolean) => void;
  onError: (isError: boolean) => void;
  dontShowPopup: boolean;
  mandateCategory: boolean;
}> = ({
  timeEntries,
  task,
  day,
  editable,
  timeEntryCategories,
  dontShowPopup,
  updateDayEntry,
  deleteDayEntry,
  addDayEntry,
  onActivate,
  onError,
  mandateCategory,
}) => {
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [timeInputVisible, setTimeInputVisible] = useState(false);
  const [showPopoverOnFocus, setPopoverOnFocus] = useState(true);

  const entries = timeEntries ?? [];

  useEffect(() => {
    if (editable && (!timeEntries || timeEntries.length === 0)) {
      addDayEntry(day.format('YYYY-MM-DD'));
    }
  }, [timeEntries, addDayEntry, day, editable]);

  const totalMintues = getTotalMinutes(entries);

  const onWrapperClick = () => {
    if (entries.length < 1) {
      return;
    }
    if (editable && entries.length <= 1) {
      setTimeInputVisible(true);
    } else if (dontShowPopup) {
      setPopoverVisible(false);
    } else if (showPopoverOnFocus) {
      setPopoverVisible(true);
    }
    setPopoverOnFocus(true);
  };

  useEffect(() => onActivate?.(popoverVisible), [onActivate, popoverVisible]);

  const hasUnStableEntries = (
    predicateFn = (val: TimesheetTableTimeEntry) =>
      isTimeEntryInCreating(val) || isTimeEntryPartiallyFilled(val),
    givenEntries?: TimesheetTableTimeEntry[],
  ) => {
    if (!mandateCategory) {
      return false;
    }
    const unstableEntries = (givenEntries || entries).find(
      (val) => predicateFn(val) && val.minutes,
    );

    if (!unstableEntries) {
      return false;
    }
    if (!unstableEntries.category?.categoryId) {
      return true;
    }

    return true;
  };

  const changePopoverState = (visible) => {
    setPopoverVisible(visible);
  };

  const addTimeEntry = () => {
    addDayEntry(day.format('YYYY-MM-DD'));
  };

  const updateTimeEntry = (timeEntry: TimesheetTableTimeEntry, values) => {
    const newEntry = { ...timeEntry, ...values };

    if (hasUnStableEntries((val) => !val.category, [newEntry])) {
      setPopoverVisible(true);
    }
    updateDayEntry(day.format('YYYY-MM-DD'), newEntry);
  };
  const deleteTimeEntry = (timeEntryId: number) => {
    if (entries.length === 1) {
      changePopoverState(false);
    }
    deleteDayEntry(day.format('YYYY-MM-DD'), timeEntryId);
  };

  const staticInputRef = useRef<Input>(null);
  const firstEntry = entries[0];
  const isFirstEntryCreating =
    entries.length !== 0 && isTimeEntryInCreating(firstEntry);
  const showLoader = isFirstEntryCreating && !popoverVisible;

  return (
    <Wrapper
      onClick={onWrapperClick}
      data-time-cell={day.format('YYYY-MM-DD')}
      data-cy={`cells.time-cell.${
        task.entryType !== TimesheetEntryType.AdhocEntry
          ? `${task.sourceId}-${day.format('ddd')}`
          : `-1.${task.activityName}-${day.format('ddd')}`
      }`}
    >
      <Popover
        trigger="click"
        placement="bottom"
        destroyTooltipOnHide
        content={
          <AddTimeEntry
            editable={editable}
            task={task}
            entries={entries}
            day={day}
            parentVisible={popoverVisible}
            timeEntryCategories={timeEntryCategories}
            addTimeEntry={addTimeEntry}
            deleteTimeEntry={deleteTimeEntry}
            updateTimeEntry={updateTimeEntry}
            onClose={(e) => {
              if (hasUnStableEntries()) {
                return;
              }
              changePopoverState(false);
              if (e.type !== 'click') {
                setPopoverOnFocus(false);
                staticInputRef?.current?.focus({ cursor: 'end' });
              }
            }}
            mandateCategory={mandateCategory}
          />
        }
        visible={popoverVisible}
        onVisibleChange={(visible) => {
          if (hasUnStableEntries()) {
            return;
          }
          changePopoverState(visible);
        }}
      >
        {timeInputVisible && firstEntry && !isFirstEntryCreating ? (
          <TimeInputBox
            autoFocus
            onErrorStateChange={onError}
            minutes={firstEntry.minutes}
            disabled={isFirstEntryCreating}
            onPressEnter={() => {
              if (hasUnStableEntries() && !dontShowPopup) {
                setPopoverVisible(true);

                return;
              }
              getNextCellToBeFocussed()?.querySelector('input')?.focus();
            }}
            onMinutesChange={(value) => {
              if (value !== firstEntry.minutes) {
                updateTimeEntry(firstEntry, {
                  minutes: value ?? 0,
                });
              }
            }}
            onBlur={() => {
              setTimeInputVisible(false);
              if (onError) {
                onError(false);
              }
            }}
            data-cy={`inputs.time-entry.${
              task.entryType !== TimesheetEntryType.AdhocEntry
                ? `${task.sourceId}-${day.format('ddd')}`
                : `-1.${task.activityName}-${day.format('ddd')}`
            }`}
          />
        ) : (
          <Time
            onClick={(e) => {
              e.stopPropagation();
              onWrapperClick();
            }}
          >
            <StaticInput
              ref={staticInputRef}
              value={totalMintues !== 0 ? formatTime(totalMintues) : '-'}
              disabled={!editable}
              onFocus={() => {
                onWrapperClick();
              }}
              data-cy="inputs.read-only-time-cell"
            />
          </Time>
        )}
        {showLoader ? <Loader /> : null}
        <SecondaryRow onClick={(e) => e.stopPropagation()}>
          {editable && !showLoader ? (
            <Tooltip
              title={
                <FormattedMessage id="timesheeets.add_multiple_entry.tooltip" />
              }
              placement="bottom"
            >
              <PopoverTrigger
                type="link"
                size="small"
                icon={<Edit16 />}
                onClick={() => {
                  changePopoverState(true);
                }}
              />
            </Tooltip>
          ) : null}
        </SecondaryRow>
      </Popover>
    </Wrapper>
  );
};
